import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Typography,
  Box,
  Chip,
  Autocomplete,
  TextField,
  IconButton,
} from '@mui/material';
import { Delete, DragIndicator } from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  pointerWithin,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  CollisionDetection,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import useOrgData from 'application/hooks/useOrgData';
import useKPIs from 'application/hooks/useKPIs';
import {
  SectionTitle,
  StyledButton,
} from '../Configuration/configurationForms/styledFormComponents';
import FormSection from 'application/components/FormSection/FormSection';
import { GamifyToast } from 'application/common/CustomToasts';
import _ from 'lodash';

const MAX_KPIS = 6;
const MIN_KPIS = 1;

interface WidgetTile {
  key: number;
  title: string;
  fullWidth: boolean;
  type: string;
  widgetOptions?: Record<string, any>;
}

interface DashboardStatsConfig {
  selectedKPIs: string[];
  kpiStatsEnabled: boolean;
  recentDealsEnabled: boolean;
  recentContactsEnabled: boolean;
  dashboardConfig?: WidgetTile[];
}

const mixedSizeCollision: CollisionDetection = (args) => {
  const pointerCollisions = pointerWithin(args);
  if (pointerCollisions.length > 0) return pointerCollisions;
  return closestCenter(args);
};

const WIDGET_META: Record<
  string,
  {
    title: string;
    description: string;
    action: string;
    route?: string;
    defaultFullWidth: boolean;
  }
> = {
  pronto: {
    title: 'Pronto',
    description: 'Deep link to Pronto messaging app',
    action: 'Open Pronto',
    route: '/pronto',
    defaultFullWidth: false,
  },
  feed: {
    title: 'Feed',
    description: 'Deep link to the activity feed',
    action: 'Open Feed',
    route: '/feed',
    defaultFullWidth: false,
  },
  appointment: {
    title: 'Appointments',
    description: 'Upcoming appointments list',
    action: 'View Appointments',
    defaultFullWidth: true,
  },
  userTask: {
    title: 'My Tasks',
    description: 'User task list widget',
    action: 'View Tasks',
    defaultFullWidth: true,
  },
  personalStats: {
    title: 'My Stats (KPIs)',
    description: 'Personal KPI stats with Today/This Month',
    action: 'View Stats',
    defaultFullWidth: true,
  },
  recentDeals: {
    title: 'Recent Deals',
    description: 'Carousel of recent deals',
    action: 'View Deals',
    defaultFullWidth: true,
  },
  webLink: {
    title: 'Web Link',
    description: 'Custom web link tile',
    action: 'Open Link',
    defaultFullWidth: false,
  },
  spacer: {
    title: 'Spacer',
    description: 'Vertical space between widgets',
    action: '',
    defaultFullWidth: true,
  },
};

const AVAILABLE_WIDGET_TYPES = Object.keys(WIDGET_META);

const DEFAULT_TILES: WidgetTile[] = [
  { key: 2, title: 'Pronto', fullWidth: false, type: 'pronto' },
  { key: 3, title: 'Feed', fullWidth: false, type: 'feed' },
  {
    key: 4,
    title: 'Appointments',
    fullWidth: true,
    type: 'appointment',
    widgetOptions: { showPast: false, daysAhead: 7 },
  },
  {
    key: 5,
    title: 'My Tasks',
    fullWidth: true,
    type: 'userTask',
    widgetOptions: { showCompleted: false, maxTasks: 5 },
  },
];

const defaultConfig: DashboardStatsConfig = {
  selectedKPIs: [],
  kpiStatsEnabled: true,
  recentDealsEnabled: true,
  recentContactsEnabled: true,
  dashboardConfig: undefined,
};

/**
 * Backwards compatibility: migrate old configs that used toggle booleans
 * into the tile-based layout. If kpiStatsEnabled was false, remove personalStats
 * tile. If recentDealsEnabled was false, remove recentDeals tile.
 */
function migrateConfig(raw: DashboardStatsConfig): DashboardStatsConfig {
  const config = _.cloneDeep(raw);
  if (!config.dashboardConfig) {
    // First time — build tiles from defaults, respecting old toggles
    const tiles = _.cloneDeep(DEFAULT_TILES);
    config.dashboardConfig = tiles.filter((t) => {
      if (t.type === 'personalStats' && config.kpiStatsEnabled === false) {
        return false;
      }
      if (t.type === 'recentDeals' && config.recentDealsEnabled === false) {
        return false;
      }
      return true;
    });
  }
  // Derive booleans from tile presence for save compatibility
  config.kpiStatsEnabled = config.dashboardConfig.some(
    (t) => t.type === 'personalStats'
  );
  config.recentDealsEnabled = config.dashboardConfig.some(
    (t) => t.type === 'recentDeals'
  );
  return config;
}

/* ─── Sortable Tile Card ─── */
const SortableTileCard = React.memo(function SortableTileCard({
  tile,
  onRemove,
  children,
}: {
  tile: WidgetTile;
  onRemove: (key: number) => void;
  onUpdateOptions?: (key: number, options: Record<string, any>) => void;
  children?: React.ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tile.key });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const meta = WIDGET_META[tile.type];

  // Spacer renders as a compact divider row
  if (tile.type === 'spacer') {
    return (
      <Box ref={setNodeRef} style={style}>
        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            p: 1,
            borderRadius: '8px',
            backgroundColor: 'transparent',
            border: `1px dashed ${theme.custom?.stroke || '#E0E0E0'}`,
          })}
        >
          <Box
            {...attributes}
            {...listeners}
            sx={(theme) => ({
              cursor: 'grab',
              display: 'flex',
              alignItems: 'center',
              color: theme.custom?.textLight,
              mr: 1,
              '&:active': { cursor: 'grabbing' },
            })}
          >
            <DragIndicator sx={{ fontSize: 16 }} />
          </Box>
          <Box
            sx={(theme) => ({
              flex: 1,
              height: '1px',
              backgroundColor: theme.custom?.stroke || '#E0E0E0',
            })}
          />
          <Typography
            sx={(theme) => ({
              fontSize: 11,
              color: theme.custom?.textLight,
              mx: 1,
            })}
          >
            spacer
          </Typography>
          <Box
            sx={(theme) => ({
              flex: 1,
              height: '1px',
              backgroundColor: theme.custom?.stroke || '#E0E0E0',
            })}
          />
          <IconButton
            size="small"
            onClick={() => onRemove(tile.key)}
            sx={(theme) => ({
              color: theme.custom?.textLight,
              p: 0.5,
              ml: 1,
              '&:hover': { color: '#d32f2f' },
            })}
          >
            <Delete sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </Box>
    );
  }

  return (
    <Box ref={setNodeRef} style={style}>
      <Box
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          p: 1.5,
          borderRadius: '12px',
          backgroundColor: theme.custom?.background2 || '#F5F5F5',
          border: `1px solid ${theme.custom?.stroke || '#E0E0E0'}`,
          minWidth: 0,
        })}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 0.5,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              minWidth: 0,
            }}
          >
            <Box
              {...attributes}
              {...listeners}
              sx={(theme) => ({
                cursor: 'grab',
                display: 'flex',
                alignItems: 'center',
                color: theme.custom?.textLight,
                '&:active': { cursor: 'grabbing' },
              })}
            >
              <DragIndicator sx={{ fontSize: 18 }} />
            </Box>
            <Typography
              sx={(theme) => ({
                fontSize: 14,
                fontWeight: 600,
                color: theme.custom?.text,
              })}
              noWrap
            >
              {meta?.title || tile.title}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            {meta?.route && (
              <Typography
                sx={(theme) => ({
                  fontSize: 11,
                  color: theme.custom?.textLight,
                  mr: 0.5,
                })}
              >
                {meta.route}
              </Typography>
            )}
            <IconButton
              size="small"
              onClick={() => onRemove(tile.key)}
              sx={(theme) => ({
                color: theme.custom?.textLight,
                p: 0.5,
                '&:hover': { color: '#d32f2f' },
              })}
            >
              <Delete sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>
        {/* Inline config for specific widget types */}
        {children}
      </Box>
    </Box>
  );
});

/* ─── Web Link Inline Config ─── */
const WebLinkConfig = React.memo(function WebLinkConfig({
  tile,
  onUpdateOptions,
}: {
  tile: WidgetTile;
  onUpdateOptions: (key: number, options: Record<string, any>) => void;
}) {
  const url = tile.widgetOptions?.url || '';
  const label = tile.widgetOptions?.label || '';

  return (
    <Box
      sx={{ mt: 1, ml: 3.5, display: 'flex', flexDirection: 'column', gap: 1 }}
    >
      <Box>
        <Typography
          sx={(theme) => ({ fontSize: 12, color: theme.custom?.text, mb: 0.5 })}
        >
          URL
        </Typography>
        <TextField
          size="small"
          placeholder="https://example.com"
          value={url}
          onChange={(e) => {
            onUpdateOptions(tile.key, {
              ...tile.widgetOptions,
              url: e.target.value,
              title: tile.widgetOptions?.label || '',
            });
          }}
          fullWidth
          sx={(theme) => ({
            '& .MuiOutlinedInput-root': {
              backgroundColor: theme.custom?.background1,
              color: theme.custom?.text,
              fontSize: 13,
              '& fieldset': {
                borderColor: theme.custom?.stroke,
                borderRadius: '8px !important',
              },
            },
          })}
        />
      </Box>
      <Box>
        <Typography
          sx={(theme) => ({ fontSize: 12, color: theme.custom?.text, mb: 0.5 })}
        >
          Label
        </Typography>
        <TextField
          size="small"
          placeholder="Button label"
          value={label}
          onChange={(e) => {
            onUpdateOptions(tile.key, {
              ...tile.widgetOptions,
              label: e.target.value,
              title: e.target.value,
            });
          }}
          fullWidth
          sx={(theme) => ({
            '& .MuiOutlinedInput-root': {
              backgroundColor: theme.custom?.background1,
              color: theme.custom?.text,
              fontSize: 13,
              '& fieldset': {
                borderColor: theme.custom?.stroke,
                borderRadius: '8px !important',
              },
            },
          })}
        />
      </Box>
    </Box>
  );
});

/* ─── KPI Inline Config ─── */
const KpiInlineConfig = React.memo(function KpiInlineConfig({
  selectedKPIs,
  KPIs,
  kpisLoading,
  onChange,
}: {
  selectedKPIs: string[];
  KPIs: any[];
  kpisLoading: boolean;
  onChange: (ids: string[]) => void;
}) {
  if (kpisLoading) {
    return (
      <Typography
        sx={(theme) => ({
          color: theme.custom?.textLight,
          fontSize: 12,
          mt: 1,
          ml: 3.5,
        })}
      >
        Loading KPIs...
      </Typography>
    );
  }
  if (KPIs.length === 0) {
    return (
      <Typography
        sx={(theme) => ({
          color: theme.custom?.textLight,
          fontSize: 12,
          mt: 1,
          ml: 3.5,
        })}
      >
        No KPI rules found.
      </Typography>
    );
  }

  return (
    <Box sx={{ mt: 1, ml: 3.5 }}>
      <Typography
        sx={(theme) => ({
          fontSize: 12,
          color: theme.custom?.textLight,
          mb: 0.5,
        })}
      >
        Select {MIN_KPIS}–{MAX_KPIS} KPIs to display:
      </Typography>
      <Autocomplete
        multiple
        disableCloseOnSelect
        size="small"
        options={KPIs.filter((kpi) => !kpi.isHidden)}
        value={KPIs.filter(
          (kpi) => !kpi.isHidden && selectedKPIs.includes(kpi.id || '')
        )}
        getOptionLabel={(option) => option.title || ''}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        onChange={(_, newValue) => {
          if (newValue.length > MAX_KPIS) {
            GamifyToast.error(`Maximum of ${MAX_KPIS} KPIs allowed`);
            return;
          }
          const ids = newValue
            .map((kpi) => kpi.id)
            .filter((id): id is string => !!id);
          onChange(ids);
        }}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.id}
              label={option.title}
              size="small"
              sx={(theme) => ({
                backgroundColor: theme.custom?.background2,
                color: theme.custom?.text,
                '& .MuiChip-deleteIcon': {
                  color: theme.custom?.textLight,
                  '&:hover': { color: theme.custom?.primary || '#FF6A00' },
                },
              })}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={selectedKPIs.length ? '' : 'Select KPIs...'}
            size="small"
          />
        )}
        sx={(theme) => ({
          '& .MuiOutlinedInput-root': {
            backgroundColor: theme.custom?.background1,
            color: theme.custom?.text,
            '& fieldset': {
              borderColor: theme.custom?.stroke,
              borderRadius: '8px !important',
            },
          },
          '& .MuiInputBase-input::placeholder': {
            color: theme.custom?.textLight,
            opacity: 1,
          },
          '& .MuiSvgIcon-root': { color: theme.custom?.textLight },
        })}
        componentsProps={{
          paper: {
            sx: (theme) => ({
              backgroundColor: theme.custom?.background1,
              color: theme.custom?.text,
            }),
          },
        }}
      />
    </Box>
  );
});

/* ─── Preview Panel ─── */
const PreviewPanel = React.memo(function PreviewPanel({
  tiles,
  selectedKPIs,
  KPIs,
}: {
  tiles: WidgetTile[];
  selectedKPIs: string[];
  KPIs: { id?: string; title?: string }[];
}) {
  return (
    <Box
      sx={(theme) => ({
        borderRadius: '16px',
        border: `1px solid ${theme.custom?.stroke || '#E0E0E0'}`,
        backgroundColor: theme.custom?.background2 || '#F5F5F5',
        p: 2.5,
        position: 'sticky',
        top: 16,
      })}
    >
      <Typography
        sx={(theme) => ({
          fontWeight: 700,
          fontSize: 18,
          color: theme.custom?.text,
          mb: 0.5,
        })}
      >
        Live Preview
      </Typography>
      <Typography
        sx={(theme) => ({
          fontSize: 13,
          color: theme.custom?.textLight,
          mb: 2,
        })}
      >
        This is how the mobile dashboard will look.
      </Typography>

      {/* Render tiles in exact array order */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {tiles.map((tile) => {
          const meta = WIDGET_META[tile.type];

          if (tile.type === 'spacer') {
            return <Box key={tile.key} sx={{ flex: '0 0 100%', height: 16 }} />;
          }

          if (tile.type === 'personalStats') {
            const selectedKpiTitles = selectedKPIs
              .map((id) => KPIs.find((k) => k.id === id)?.title)
              .filter(Boolean);
            return (
              <Box
                key={tile.key}
                sx={(theme) => ({
                  flex: '0 0 100%',
                  borderRadius: '12px',
                  border: `1px solid ${theme.custom?.stroke || '#E0E0E0'}`,
                  backgroundColor: theme.custom?.background1 || '#FFF',
                  p: 1.5,
                })}
              >
                <Typography
                  sx={(theme) => ({
                    fontSize: 13,
                    fontWeight: 600,
                    color: theme.custom?.text,
                    mb: 0.5,
                  })}
                >
                  My Stats
                </Typography>
                {selectedKpiTitles.length > 0 ? (
                  selectedKpiTitles.slice(0, 3).map((title) => (
                    <Box
                      key={title}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        py: 0.25,
                      }}
                    >
                      <Typography
                        sx={(theme) => ({
                          fontSize: 12,
                          color: theme.custom?.textLight,
                        })}
                      >
                        {title}
                      </Typography>
                      <Typography
                        sx={(theme) => ({
                          fontSize: 12,
                          color: theme.custom?.text,
                          fontWeight: 500,
                        })}
                      >
                        $0
                      </Typography>
                    </Box>
                  ))
                ) : (
                  <Typography
                    sx={(theme) => ({
                      fontSize: 12,
                      color: theme.custom?.textLight,
                    })}
                  >
                    No KPIs selected
                  </Typography>
                )}
              </Box>
            );
          }

          if (tile.type === 'webLink') {
            const label = tile.widgetOptions?.label || 'Web Link';
            const url = tile.widgetOptions?.url;
            return (
              <Box
                key={tile.key}
                sx={(theme) => ({
                  flex: '0 0 calc(50% - 4px)',
                  borderRadius: '12px',
                  backgroundColor: theme.custom?.primary || '#FF6A00',
                  p: 1.5,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                })}
              >
                <Typography
                  sx={(theme) => ({
                    fontSize: 13,
                    fontWeight: 600,
                    color: theme.custom?.textCTA || '#FFF',
                  })}
                  noWrap
                >
                  {label}
                </Typography>
                <Typography
                  sx={(theme) => ({
                    fontSize: 11,
                    color: theme.custom?.textCTA || '#FFF',
                    opacity: 0.8,
                  })}
                  noWrap
                >
                  {url || 'No URL configured'}
                </Typography>
              </Box>
            );
          }

          /* Half-width tiles (feed, messages, recentDeals, etc.) */
          if (!tile.fullWidth) {
            return (
              <Box
                key={tile.key}
                sx={(theme) => ({
                  flex: '0 0 calc(50% - 4px)',
                  borderRadius: '12px',
                  backgroundColor: theme.custom?.primary || '#FF6A00',
                  p: 1.5,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                })}
              >
                <Typography
                  sx={(theme) => ({
                    fontSize: 13,
                    fontWeight: 600,
                    color: theme.custom?.textCTA || '#FFF',
                  })}
                  noWrap
                >
                  {tile.title}
                </Typography>
                <Typography
                  sx={(theme) => ({
                    fontSize: 11,
                    color: theme.custom?.textCTA || '#FFF',
                    opacity: 0.8,
                  })}
                  noWrap
                >
                  {meta?.action || tile.type}
                </Typography>
              </Box>
            );
          }

          /* Other full-width tiles */
          return (
            <Box
              key={tile.key}
              sx={(theme) => ({
                flex: '0 0 100%',
                borderRadius: '12px',
                border: `1px solid ${theme.custom?.stroke || '#E0E0E0'}`,
                backgroundColor: theme.custom?.background1 || '#FFF',
                p: 1.5,
              })}
            >
              <Typography
                sx={(theme) => ({
                  fontSize: 13,
                  fontWeight: 600,
                  color: theme.custom?.text,
                })}
              >
                {tile.title}
              </Typography>
              <Typography
                sx={(theme) => ({
                  fontSize: 11,
                  color: theme.custom?.textLight,
                })}
              >
                {meta?.description || tile.type}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
});

/* ─── Main Component ─── */
export default function DashboardConfig() {
  const { orgData, updateOrgData } = useOrgData();
  const { KPIs, isLoading: kpisLoading } = useKPIs();
  const [config, setConfig] = useState<DashboardStatsConfig>(defaultConfig);
  const [hasChanged, setHasChanged] = useState(false);

  const savedConfig: DashboardStatsConfig =
    orgData?.configParms?.dashboardStats || defaultConfig;

  useEffect(() => {
    if (orgData) {
      const existing = orgData.configParms?.dashboardStats;
      if (existing) {
        setConfig(migrateConfig(existing));
      }
    }
  }, [orgData]);

  useEffect(() => {
    setHasChanged(!_.isEqual(config, savedConfig));
  }, [config, savedConfig]);

  const handleSave = useCallback(async () => {
    const hasStats = tiles.some((t) => t.type === 'personalStats');
    if (hasStats && config.selectedKPIs.length < MIN_KPIS) {
      GamifyToast.error(`Please select at least ${MIN_KPIS} KPI`);
      return;
    }

    // Derive booleans for backwards compat with mobile app
    const configToSave: DashboardStatsConfig = {
      ...config,
      kpiStatsEnabled: tiles.some((t) => t.type === 'personalStats'),
      recentDealsEnabled: tiles.some((t) => t.type === 'recentDeals'),
      recentContactsEnabled: config.recentContactsEnabled,
    };

    const newConfigParms = {
      ...orgData?.configParms,
      dashboardStats: configToSave,
    };
    const newOrgInput = {
      ...orgData,
      configParms: JSON.stringify(newConfigParms),
    };
    const success = await updateOrgData(newOrgInput);
    if (success) {
      GamifyToast.success('Dashboard configuration saved');
    } else {
      GamifyToast.error('Failed to save dashboard configuration');
    }
  }, [config, orgData, updateOrgData]);

  const tiles: WidgetTile[] = config.dashboardConfig || DEFAULT_TILES;

  const setTiles = useCallback((newTiles: WidgetTile[]) => {
    setConfig((prev) => ({
      ...prev,
      dashboardConfig: newTiles,
      // Keep booleans in sync for change detection
      kpiStatsEnabled: newTiles.some((t) => t.type === 'personalStats'),
      recentDealsEnabled: newTiles.some((t) => t.type === 'recentDeals'),
    }));
  }, []);

  const handleAddWidget = useCallback(
    (widgetDef: { type: string; title: string; defaultFullWidth: boolean }) => {
      const nextKey =
        tiles.length > 0 ? Math.max(...tiles.map((t) => t.key)) + 1 : 1;
      const newTile: WidgetTile = {
        key: nextKey,
        title: widgetDef.title,
        fullWidth: widgetDef.defaultFullWidth,
        type: widgetDef.type,
      };
      setTiles([...tiles, newTile]);
    },
    [tiles, setTiles]
  );

  const handleRemoveByKey = useCallback(
    (key: number) => {
      setTiles(tiles.filter((t) => t.key !== key));
    },
    [tiles, setTiles]
  );

  const handleUpdateOptions = useCallback(
    (key: number, options: Record<string, any>) => {
      setTiles(
        tiles.map((t) => (t.key === key ? { ...t, widgetOptions: options } : t))
      );
    },
    [tiles, setTiles]
  );

  const handleKpiChange = useCallback((ids: string[]) => {
    setConfig((prev) => ({ ...prev, selectedKPIs: ids }));
  }, []);

  // DnD
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }
      const oldIndex = tiles.findIndex((t) => t.key === active.id);
      const newIndex = tiles.findIndex((t) => t.key === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        setTiles(arrayMove(tiles, oldIndex, newIndex));
      }
    },
    [tiles, setTiles]
  );

  const addableWidgets = useMemo(
    () =>
      AVAILABLE_WIDGET_TYPES.filter(
        (type) =>
          type !== 'spacer' &&
          type !== 'webLink' &&
          !tiles.some((t) => t.type === type)
      ).map((type) => ({
        type,
        title: WIDGET_META[type].title,
        description: WIDGET_META[type].description,
        defaultFullWidth: WIDGET_META[type].defaultFullWidth,
      })),
    [tiles]
  );

  const handleAddSpacer = useCallback(() => {
    const nextKey =
      tiles.length > 0 ? Math.max(...tiles.map((t) => t.key)) + 1 : 1;
    setTiles([
      ...tiles,
      { key: nextKey, title: 'Spacer', fullWidth: true, type: 'spacer' },
    ]);
  }, [tiles, setTiles]);

  const handleAddWebLink = useCallback(() => {
    const nextKey =
      tiles.length > 0 ? Math.max(...tiles.map((t) => t.key)) + 1 : 1;
    setTiles([
      ...tiles,
      {
        key: nextKey,
        title: 'Web Link',
        fullWidth: false,
        type: 'webLink',
        widgetOptions: { url: '', label: '', title: '' },
      },
    ]);
  }, [tiles, setTiles]);

  const tileIds = useMemo(() => tiles.map((t) => t.key), [tiles]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Header row with Save */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography
            sx={(theme) => ({
              fontWeight: 780,
              fontSize: 20,
              color: theme.custom?.text,
            })}
          >
            Dashboard Configuration
          </Typography>
          <Typography
            sx={(theme) => ({
              fontWeight: 400,
              fontSize: 15,
              color: theme.custom?.textLight,
            })}
          >
            Configure stats and layout for the mobile dashboard.
          </Typography>
        </Box>
        <StyledButton
          onClick={handleSave}
          disabled={!hasChanged}
          sx={{ width: 'auto', px: 4 }}
        >
          Save
        </StyledButton>
      </Box>

      {/* Two-column layout */}
      <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
        {/* Left column — Layout Builder */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <FormSection>
            <SectionTitle sx={{ mb: 0.5 }}>Layout Builder</SectionTitle>
            <Typography
              sx={(theme) => ({
                fontSize: 13,
                color: theme.custom?.textLight,
                mb: 2,
              })}
            >
              Drag and drop to reorder widgets. Add or remove tiles to customize
              the mobile dashboard.
            </Typography>

            <DndContext
              sensors={sensors}
              collisionDetection={mixedSizeCollision}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={tileIds}
                strategy={verticalListSortingStrategy}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    mb: 2,
                  }}
                >
                  {tiles.map((tile) => {
                    const isHalf = !tile.fullWidth && tile.type !== 'spacer';
                    return (
                      <Box
                        key={tile.key}
                        sx={{
                          flex: isHalf ? '0 0 calc(50% - 4px)' : '0 0 100%',
                        }}
                      >
                        <SortableTileCard
                          tile={tile}
                          onRemove={handleRemoveByKey}
                          onUpdateOptions={handleUpdateOptions}
                        >
                          {tile.type === 'personalStats' && (
                            <KpiInlineConfig
                              selectedKPIs={config.selectedKPIs}
                              KPIs={KPIs}
                              kpisLoading={kpisLoading}
                              onChange={handleKpiChange}
                            />
                          )}
                          {tile.type === 'webLink' && (
                            <WebLinkConfig
                              tile={tile}
                              onUpdateOptions={handleUpdateOptions}
                            />
                          )}
                        </SortableTileCard>
                      </Box>
                    );
                  })}
                </Box>
              </SortableContext>
            </DndContext>

            {/* Add Widget */}
            <Autocomplete
              key={tiles.length}
              options={addableWidgets}
              getOptionLabel={(option) => option.title}
              onChange={(_, value) => {
                if (value) {
                  handleAddWidget(value);
                }
              }}
              value={null}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="+ Add Widget"
                  size="small"
                />
              )}
              sx={(theme) => ({
                '& .MuiOutlinedInput-root': {
                  backgroundColor: theme.custom?.background1,
                  color: theme.custom?.text,
                  '& fieldset': {
                    borderColor: theme.custom?.stroke,
                    borderStyle: 'dashed',
                    borderRadius: '8px !important',
                  },
                },
                '& .MuiInputBase-input::placeholder': {
                  color: theme.custom?.textLight,
                  opacity: 1,
                },
                '& .MuiSvgIcon-root': { color: theme.custom?.textLight },
              })}
              componentsProps={{
                paper: {
                  sx: (theme) => ({
                    backgroundColor: theme.custom?.background1,
                    color: theme.custom?.text,
                  }),
                },
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.type}>
                  <Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                      {option.title}
                    </Typography>
                    <Typography
                      sx={(theme) => ({
                        fontSize: 12,
                        color: theme.custom?.textLight,
                      })}
                    >
                      {option.description}
                    </Typography>
                  </Box>
                </li>
              )}
            />

            {/* Add Spacer / Web Link */}
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Box
                onClick={handleAddWebLink}
                sx={(theme) => ({
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 0.75,
                  borderRadius: '8px',
                  border: `1px dashed ${theme.custom?.stroke || '#E0E0E0'}`,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: theme.custom?.background2 || '#F5F5F5',
                  },
                })}
              >
                <Typography
                  sx={(theme) => ({
                    fontSize: 13,
                    color: theme.custom?.textLight,
                  })}
                >
                  + Add Web Link
                </Typography>
              </Box>
              <Box
                onClick={handleAddSpacer}
                sx={(theme) => ({
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 0.75,
                  borderRadius: '8px',
                  border: `1px dashed ${theme.custom?.stroke || '#E0E0E0'}`,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: theme.custom?.background2 || '#F5F5F5',
                  },
                })}
              >
                <Typography
                  sx={(theme) => ({
                    fontSize: 13,
                    color: theme.custom?.textLight,
                  })}
                >
                  + Add Spacer
                </Typography>
              </Box>
            </Box>
          </FormSection>
        </Box>

        {/* Right column — live preview */}
        <Box sx={{ width: 340, flexShrink: 0 }}>
          <PreviewPanel
            tiles={tiles}
            selectedKPIs={config.selectedKPIs}
            KPIs={KPIs}
          />
        </Box>
      </Box>
    </Box>
  );
}
